import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';

// The all-time daily device-state rollup, from the usageAnalyticsDaily
// collection written by the /admin/updateUsageAnalytics sweep.
//
// This is a SEPARATE collection from usageAnalytics on purpose. The Accounts
// tab reads all of usageAnalytics on every visit — the Firestore Web SDK has no
// field projection, so it is all-or-nothing — and the daily rollup is the one
// part of the analysis that grows without bound as history accumulates. Keeping
// it out of that document means the Default, Usage and Billing views never
// download a byte of it; only the History view, which renders it, pays.
//
// Loaded once when the History view opens (and re-loadable after a sweep),
// rather than subscribed: the data only changes when an admin presses
// "Update analysis".

export function useAccountDailyStates(enabled) {
  const [dailyStates, setDailyStates] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Must be re-armed on every mount, not just the first: React 18 StrictMode
  // mounts → unmounts → re-mounts in development.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchDailyStates = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, 'usageAnalyticsDaily'));
      const byFamily = {};
      snap.docs.forEach((d) => { byFamily[d.id] = d.data(); });
      if (!mountedRef.current) return;
      setDailyStates(byFamily);
      setError(null);
    } catch (e) {
      // Keep whatever we already hold. The most likely cause on a first run is
      // that firestore.rules has not been deployed with the new collection, so
      // say that rather than leaving the strips silently blank — blank is
      // otherwise indistinguishable from "the sweep hasn't computed this yet".
      console.warn('usageAnalyticsDaily fetch failed:', e.code || e.message);
      if (mountedRef.current) {
        setError(e.code === 'permission-denied'
          ? 'Device history could not be read. If usageAnalyticsDaily is a new collection here, firestore.rules may not be deployed yet.'
          : 'Device history could not be read.');
      }
    } finally {
      if (mountedRef.current) setLoaded(true);
    }
  }, []);

  // First activation only, plus whenever the data has been invalidated. The
  // collection changes exactly when someone presses "Update analysis", so
  // toggling between Usage and History must not re-download every family
  // document nor flash the whole table back through its loading state.
  const fetchedRef = useRef(false);
  const [staleToken, setStaleToken] = useState(0);
  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchDailyStates();
  }, [enabled, fetchDailyStates, staleToken]);

  /**
   * Mark what we hold as out of date.
   *
   * A sweep pressed from the Usage or Billing view must still be reflected the
   * next time History is opened — otherwise the header (which re-reads _meta on
   * every press) reports a deeper backfill than the strips are drawing, and an
   * admin pressing the button repeatedly to walk history back watches the
   * counter advance while the chart never moves. Refetching immediately only
   * when History is on screen keeps the "never download it for admins who
   * don't open the view" property.
   */
  const invalidate = useCallback(() => {
    fetchedRef.current = false;
    setStaleToken(t => t + 1);
  }, []);

  return {
    dailyStates,
    loading: enabled && !loaded,
    error,
    refresh: fetchDailyStates,
    invalidate,
  };
}
